import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import { feedAPI } from '../services/api';
import { toArray } from '../utils/apiData';
import { CardSkeleton } from './LoadingSkeletons';

const HorseFeedSchedule = ({ horseId }) => {
  const [feedRequirements, setFeedRequirements] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadFeedSchedule = useCallback(async () => {
    try {
      setLoading(true);
      const response = await feedAPI.getAll({ horse_id: horseId });
      setFeedRequirements(toArray(response.data, 'feed schedule'));
    } catch (error) {
      console.error('Error loading feed schedule:', error);
    } finally {
      setLoading(false);
    }
  }, [horseId]);

  useEffect(() => {
    loadFeedSchedule();
  }, [loadFeedSchedule]);

  // Group requirements by feed_name and sum daily amounts
  const groupedByFeedName = feedRequirements.reduce((acc, req) => {
    const key = req.feed_name_id || req.feed_name || 'unknown';
    if (!acc[key]) {
      acc[key] = {
        ...req,
        records: [req],
        totalDailyAmount: req.daily_amount || 0,
        allMealTimes: new Set(),
      };
    } else {
      acc[key].records.push(req);
      acc[key].totalDailyAmount += (req.daily_amount || 0);
    }
    
    // Collect all meal times for this feed name
    if (req.morning_feed) acc[key].allMealTimes.add('Morning');
    if (req.midday_feed) acc[key].allMealTimes.add('Midday');
    if (req.evening_feed) acc[key].allMealTimes.add('Evening');
    
    return acc;
  }, {});
  
  const groupedRequirements = Object.values(groupedByFeedName)
    .map(group => ({
      ...group,
      allMealTimes: Array.from(group.allMealTimes).join(', ') || '-'
    }))
    .sort((a, b) => {
      // First sort by feed_type
      const typeA = (a.feed_type || '').toLowerCase();
      const typeB = (b.feed_type || '').toLowerCase();
      if (typeA !== typeB) {
        return typeA.localeCompare(typeB);
      }
      // Then sort by feed_name
      const nameA = (a.feed_name || '').toLowerCase();
      const nameB = (b.feed_name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

  if (loading) {
    return <CardSkeleton label="Loading feed schedule…" />;
  }

  return (
    <Card>
      <CardContent>
        {feedRequirements.length === 0 ? (
          <Typography color="textSecondary">No feed schedule configured</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Feed Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Feedings/Day</TableCell>
                  <TableCell align="right">Daily Total</TableCell>
                  <TableCell>Feeding Times</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedRequirements.map((group) => (
                  <TableRow key={group.feed_name_id || group.feed_name || group.id} sx={{ opacity: group.active ? 1 : 0.5 }}>
                    <TableCell>
                      <strong>{group.feed_name || 'Unknown'}</strong>
                      {group.special_instructions && (
                        <Typography variant="caption" display="block" color="textSecondary">
                          {group.special_instructions}
                        </Typography>
                      )}
                      {group.records.length > 1 && (
                        <Typography variant="caption" display="block" color="textSecondary">
                          ({group.records.length} records)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{group.feed_type || '-'}</TableCell>
                    <TableCell>{group.brand || '-'}</TableCell>
                    <TableCell align="right">
                      {group.records.length > 1 ? (
                        group.records.map((record, idx) => (
                          <Typography key={idx} variant="body2" component="div">
                            {record.amount || '-'} {record.unit || 'lbs'}
                          </Typography>
                        ))
                      ) : (
                        <>
                          {group.amount ? (
                            <>
                              {group.amount} {group.unit || 'lbs'}
                            </>
                          ) : (
                            '-'
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {group.records.length > 1 ? (
                        group.records.map((record, idx) => (
                          <Typography key={idx} variant="body2" component="div">
                            {record.feedings_per_day || '-'}
                          </Typography>
                        ))
                      ) : (
                        group.feedings_per_day || '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {group.totalDailyAmount ? (
                        <>
                          <strong>{group.totalDailyAmount} {group.unit || 'lbs'}</strong>
                        </>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{group.allMealTimes}</TableCell>
                    <TableCell>
                      <Chip
                        label={group.active ? 'Active' : 'Inactive'}
                        size="small"
                        color={group.active ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {feedRequirements.length > 0 && feedRequirements.some(req => req.supplements) && (
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Supplements:
            </Typography>
            {feedRequirements
              .filter(req => req.supplements)
              .map(req => (
                <Typography key={req.id} variant="body2" color="textSecondary">
                  • {req.supplements}
                </Typography>
              ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default HorseFeedSchedule;


